import React, { useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { css, cx } from '@emotion/css'
import { theme as deprecatedTheme } from '../../styled'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { CSSTransition } from 'react-transition-group'
import {
  buildModifiers,
  PopperRefHandler,
  cssTransitionTimeouts,
  fadeCss,
  transitionName
} from '../../legacy-design-system/popper'
import { OnboardingPanelContent } from './onboarding-panel-content'
import {
  PopperArrow,
  largeArrowHeight
} from '../../legacy-design-system/popper-arrow'
import { ItemProps, OnboardingItem } from './onboarding-item'
import {
  getApplicationsRoute,
  getAttacksRoute,
  getVulnerabilitiesRoute,
  getLibrariesRoute,
  getApplicationLibrariesRoute,
  getApplicationVulnerabilitiesRoute,
  getWizardRoute,
  updateRoute
} from '../route'
import { usePopper } from 'react-popper'
import { Props as IntlProps } from 'react-intl/src/components/message'

type OnboardingSteps =
  | 'ONBOARDING'
  | 'EXERCISE_APP'
  | 'VULNERABILITIES'
  | 'LIBRARIES'
  | 'ATTACKS'

export type OnboardingStatus = 'INACTIVE' | 'ACTIVE' | 'COMPLETED'

export const OnboardingStepsKeys: OnboardingSteps[] = [
  'ONBOARDING',
  'EXERCISE_APP',
  'VULNERABILITIES',
  'LIBRARIES',
  'ATTACKS'
]

export type OnboardingStepsBreakdown = Record<OnboardingSteps, OnboardingStatus>

const panelTitle = {
  id: 'title_onboardingSetup_setup'
}

interface Props {
  readonly organizationID: string
  readonly applicationID?: string
  readonly children: (props: {
    readonly ref?: PopperRefHandler
    readonly onClick?: () => void
  }) => React.ReactElement<any>
  readonly breakdown: OnboardingStepsBreakdown
}

export interface State {
  readonly isOpen: boolean
}

const parentContainerCss = css`
  display: inline-block;
`

const getItemsConfig = (
  intl: IntlShape,
  breakdown: OnboardingStepsBreakdown,
  orgID: string,
  appID?: string
): ItemProps[] => [
  {
    title: {
      id: 'title_onboardingSetup_onboardingStep'
    },
    description: {
      id: 'detail_onboardingSetup_onboardingStep'
    },
    completedMessage: {
      id: 'detail_onboardingSetup_onboardingStepCompleted'
    },
    status: breakdown.ONBOARDING,

    onClose: () => updateRoute(getWizardRoute(orgID))
  },
  {
    title: {
      id: 'title_onboardingSetup_exerciseStep'
    },
    description: {
      id: 'detail_onboardingSetup_exerciseStep'
    },
    completedMessage: {
      id: 'detail_onboardingSetup_exerciseStepCompleted'
    },
    status: breakdown.EXERCISE_APP,

    onClose: () => updateRoute(getApplicationsRoute(orgID))
  },
  {
    title: {
      id: 'title_onboardingSetup_vulnerabilitiesStep'
    },
    description: {
      id: 'detail_onboardingSetup_vulnerabilitiesStep'
    },
    completedMessage: {
      id: 'detail_onboardingSetup_vulnerabilitiesStepCompleted'
    },
    status: breakdown.VULNERABILITIES,

    onClose: () =>
      appID
        ? updateRoute(getApplicationVulnerabilitiesRoute(orgID, appID))
        : updateRoute(getVulnerabilitiesRoute(orgID))
  },
  {
    title: {
      id: 'title_onboardingSetup_librariesStep'
    },
    description: {
      id: 'detail_onboardingSetup_librariesStep'
    },
    completedMessage: {
      id: 'detail_onboardingSetup_librariesStepCompleted'
    },
    status: breakdown.LIBRARIES,

    onClose: () =>
      appID
        ? updateRoute(getApplicationLibrariesRoute(orgID, appID))
        : updateRoute(getLibrariesRoute(orgID))
  },
  {
    title: {
      id: 'title_onboardingSetup_attacksStep'
    },
    description: {
      id: 'variable_onboardingSetup_attacksStep',
      values: {
        a: (msg) => (
          <a
            target="_blank"
            href={intl.formatMessage({ id: 'link_runAttackScripts' })}
            rel="noopener noreferrer"
          >
            {msg}
          </a>
        )
      }
    },
    completedMessage: {
      id: 'detail_onboardingSetup_attacksStepCompleted'
    },
    status: breakdown.ATTACKS,

    onClose: () => updateRoute(getAttacksRoute(orgID))
  }
]

type TooltipElement = HTMLElement | null

const OnboardingPanel = ({
  children,
  breakdown,
  organizationID,
  applicationID
}: Props & {
  readonly children: (props: {
    readonly ref: React.Ref<any>
    readonly onClick: () => void
  }) => React.ReactNode
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const [referenceElement, setReferenceElement] = useState<TooltipElement>(null)
  const [popperElement, setPopperElement] = useState<TooltipElement>(null)
  const [arrowElement, setArrowElement] = useState<TooltipElement>(null)

  const placement = 'bottom'

  const intl = useIntl()
  const {
    styles,
    attributes: { popper: popperAttributes }
  } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      ...buildModifiers(largeArrowHeight)
    ]
  })

  const toggle = () => {
    // Prevent user from toggling while a toggle transition is active
    if (isTransitioning) {
      return
    }

    setIsOpen(!isOpen)
  }

  const itemsConfig = getItemsConfig(
    intl,
    breakdown,
    organizationID,
    applicationID
  )

  return (
    <span className={parentContainerCss}>
      {children({
        ref: setReferenceElement,
        onClick: () => toggle()
      })}
      <CSSTransition
        unmountOnExit
        in={isOpen}
        timeout={cssTransitionTimeouts}
        isTransitioning={isTransitioning}
        classNames={transitionName}
        appear={true}
        onEnter={() => setIsTransitioning(true)}
        onEntered={() => setIsTransitioning(false)}
        onExit={() => setIsTransitioning(true)}
        onExited={() => setIsTransitioning(false)}
      >
        <React.Fragment>
          <span
            ref={setPopperElement}
            style={styles.popper}
            className={cx(
              fadeCss,
              css`
                padding: 0px;
                font-family: ${deprecatedTheme.fontFamilies.secondary};
                font-size: 13px;
                color: ${theme.color.neutral100};
                background-color: ${theme.color.white};
                border-radius: 4px;
                box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
              `
            )}
            {...popperAttributes}
          >
            <OnboardingPanelContent
              onClose={toggle}
              title={panelTitle}
              subtitle={getPanelSubtitle(breakdown)}
              children={itemsConfig.map(
                (
                  {
                    title,
                    description,
                    completedMessage,
                    status,
                    onClose
                  }: ItemProps,
                  index
                ) => (
                  <OnboardingItem
                    key={index}
                    title={title}
                    description={description}
                    completedMessage={completedMessage}
                    status={status}
                    onClose={onClose}
                  />
                )
              )}
            />
            <PopperArrow
              placement={placement}
              getRef={setArrowElement}
              arrowColor="white"
              style={{
                ...styles.arrow,
                width: 0,
                height: 0,
                borderStyle: 'solid',
                position: 'absolute'
              }}
              arrowBorderColor={theme.color.white}
              arrowBorderWidth="10px"
              arrowShadowColor={theme.color.neutral40}
            />
          </span>
        </React.Fragment>
      </CSSTransition>
    </span>
  )
}

const getPanelSubtitle = (breakdown: OnboardingStepsBreakdown): IntlProps => {
  const steps = OnboardingStepsKeys.filter((k) => breakdown[k])
  const stepsStatuses = steps.map((k) => breakdown[k])
  const completedSteps = stepsStatuses.filter((k) => k === 'COMPLETED')

  return {
    id: 'variable_onboardingSetup_stepsCompleted',
    values: { stepsCompleted: completedSteps.length, totalSteps: steps.length }
  }
}

export { OnboardingPanel }
